import { Radio } from "@headlessui/react";
import { type Dtos, pluralize, type SfaaCodeId, SuretyType } from "@inrev/common";
import { type ReactNode, useMemo, useState } from "react";
import { OptionBadge } from "../../form/inputs/OptionBadge";
import { SearchField, searchFieldInputClass } from "../../form/inputs/SearchField";
import { SfaaCodeOwnerTypeFilter } from "../../form/inputs/SfaaCodeOwnerTypeFilter";
import { cn } from "../../lib/utils";
import { FloatingPanel, panelScrollRegionProps } from "../../patterns/floating-panel";
import { Button, ClickableText, Icon, RadioGroup, SelectionDot, Text } from "../../primitives";
import { trackingAttrs } from "../../utils";
import {
	equivalentSfaaCode,
	isSfaaCodeForOtherOwner,
	isSfaaContractObligee,
	matchesSfaaObligee,
	type SfaaContractObligee,
	sfaaContractObligeeLabel,
} from "./sfaaCodeFilters";

type SfaaCodeRow = Dtos.SfaaCode.Get.Response;

export type SfaaCodeDirectoryProps = {
	sfaaCodes: Dtos.SfaaCode.List.Response;
	suretyType: SuretyType;
	initialOwnerType?: SfaaContractObligee;
	value?: SfaaCodeId;
	onAttach: (value: SfaaCodeId) => void;
	onClose: () => void;
};

type TypeChoice = { key: string; type: string; subType: string; count: number };
type SearchGroup = { key: string; type: string; subType: string; rows: SfaaCodeRow[] };

const typeKeyOf = ({ type, subType }: SfaaCodeRow) => `${type}\u0000${subType}`;

const groupBy = <T,>(items: readonly T[], keyOf: (item: T) => string) => {
	const groups = new Map<string, T[]>();
	for (const item of items) {
		const key = keyOf(item);
		const group = groups.get(key) ?? [];
		group.push(item);
		groups.set(key, group);
	}
	return [...groups.entries()];
};

const byCode = (a: SfaaCodeRow, b: SfaaCodeRow) =>
	a.code.localeCompare(b.code) || a.label.localeCompare(b.label);

const matchesSearch = (row: SfaaCodeRow, query: string) =>
	[row.code, row.label, row.type, row.subType, row.class, row.subClass].some((value) =>
		value.toLocaleLowerCase().includes(query),
	);

const codePath = (row: SfaaCodeRow) => {
	const trail = [row.type, row.subType, row.class, row.subClass].filter(Boolean).join(" > ");
	return row.obligeeType ? `${row.obligeeType} · ${trail}` : trail;
};

const FirstLine = ({ children }: { children: ReactNode }) => (
	<div className="flex min-h-5 min-w-0 items-center">{children}</div>
);

const CodeRadio = ({ row, detail }: { row: SfaaCodeRow; detail?: string }) => (
	<Radio
		value={row.id}
		data-testid="sfaa-directory-leaf"
		className={cn(
			"flex w-full cursor-pointer items-start gap-3 border-b border-bc-subtle px-4 py-2 text-left last:border-b-0 hover:bg-surface-light data-[checked]:bg-surface-accent",
			detail && "px-5",
		)}
	>
		{({ checked }) => (
			<>
				<FirstLine>
					<SelectionDot checked={checked} size="md" />
				</FirstLine>
				<div className="flex min-w-0 flex-1 items-start gap-2">
					<FirstLine>
						<OptionBadge badge={row.code} />
					</FirstLine>
					<div className="flex min-w-0 flex-1 flex-col gap-1">
						<FirstLine>
							<Text type="body" color="primary">
								{row.label}
							</Text>
						</FirstLine>
						{detail && (
							<Text type="caption" color="tertiary">
								{detail}
							</Text>
						)}
					</div>
					{!detail && row.obligeeType && (
						<FirstLine>
							<Text type="caption" color="tertiary">
								{row.obligeeType}
							</Text>
						</FirstLine>
					)}
				</div>
			</>
		)}
	</Radio>
);

const DirectoryHeader = () => (
	<FloatingPanel.Header>
		<div className="min-w-0 flex-1">
			<FloatingPanel.Title>SFAA Code Directory</FloatingPanel.Title>
			<FloatingPanel.Subtitle>
				Browse by bond type, or search by code, keyword, or class
			</FloatingPanel.Subtitle>
		</div>
		<FloatingPanel.Actions />
	</FloatingPanel.Header>
);

const DirectoryToolbar = ({
	query,
	onQueryChange,
	suretyType,
	ownerType,
	onOwnerTypeChange,
}: {
	query: string;
	onQueryChange: (query: string) => void;
	suretyType: SuretyType;
	ownerType: SfaaContractObligee;
	onOwnerTypeChange: (ownerType: SfaaContractObligee) => void;
}) => (
	<div className="flex shrink-0 items-center gap-4 border-b border-bc-default px-5 py-3">
		<SearchField className="flex-1 rounded-md border border-bc-default bg-surface-base px-3">
			<input
				type="search"
				value={query}
				onChange={(event) => onQueryChange(event.target.value)}
				placeholder={
					suretyType === SuretyType.contract
						? `Search ${sfaaContractObligeeLabel(ownerType)} codes`
						: "Search all codes"
				}
				aria-label="Search SFAA codes"
				data-testid="sfaa-directory-search"
				{...trackingAttrs("sfaa-directory-search")}
				className={cn(searchFieldInputClass, "h-9 text-[14px]")}
			/>
		</SearchField>
		{suretyType === SuretyType.contract && (
			<div className="shrink-0">
				<SfaaCodeOwnerTypeFilter value={ownerType} onChange={onOwnerTypeChange} />
			</div>
		)}
	</div>
);

const SearchResults = ({
	query,
	groups,
	selectedId,
	onSelect,
	ownerLabel,
}: {
	query: string;
	groups: SearchGroup[];
	selectedId: SfaaCodeId | null;
	onSelect: (id: SfaaCodeId | null) => void;
	ownerLabel?: string;
}) => (
	<div
		{...panelScrollRegionProps}
		className="min-h-0 flex-1 overflow-y-auto"
		data-testid="sfaa-directory-results"
	>
		{groups.length === 0 ? (
			<Text type="body" color="tertiary" className="block px-5 py-4">
				{ownerLabel
					? `No results for "${query.trim()}" in ${ownerLabel}`
					: `No results for "${query.trim()}"`}
			</Text>
		) : (
			<RadioGroup
				value={selectedId}
				onChange={onSelect}
				direction="column"
				className="gap-0"
				aria-label="SFAA search results"
			>
				{groups.map((group) => (
					<section key={group.key}>
						<div className="bg-surface-neutral px-5 py-4">
							<Text type="caption" color="secondary" className="block leading-5">
								{`${group.type} - ${group.subType} (${pluralize(group.rows.length, "result", "results")})`}
							</Text>
						</div>
						{group.rows.map((row) => (
							<CodeRadio key={row.id} row={row} detail={codePath(row)} />
						))}
					</section>
				))}
			</RadioGroup>
		)}
	</div>
);

type OutlineItem = { key: string; label: string; count: number };
type OutlineGroup = { key: string; label?: string; items: OutlineItem[] };

const OutlineColumn = ({
	name,
	title,
	widthClass,
	collapsed,
	onToggle,
	groups,
	activeKey,
	onSelect,
	showChevron = false,
}: {
	name: string;
	title: string;
	widthClass: string;
	collapsed: boolean;
	onToggle: () => void;
	groups: OutlineGroup[];
	activeKey?: string;
	onSelect: (key: string) => void;
	showChevron?: boolean;
}) => {
	const slug = name.toLowerCase();
	return (
		<section
			className={cn(
				"flex min-h-0 shrink-0 flex-col bg-surface-neutral transition-[width]",
				collapsed ? "w-12" : widthClass,
			)}
			data-testid={`sfaa-directory-${slug}-pane`}
		>
			<div
				className={cn(
					"flex h-10 shrink-0 items-center px-2",
					collapsed ? "justify-end" : "justify-between",
				)}
			>
				{!collapsed && (
					<Text type="caption" color="tertiary" className="min-w-0 truncate">
						{title}
					</Text>
				)}
				<Button
					variant="ghost"
					size="sm"
					aria-label={collapsed ? `Expand ${name} column` : `Collapse ${name} column`}
					data-testid={`sfaa-directory-toggle-${slug}`}
					trackingId={`sfaa-directory-toggle-${slug}`}
					onClick={onToggle}
				>
					<Icon type={collapsed ? "chevron-right" : "chevron-left"} size="mini" />
				</Button>
			</div>
			{!collapsed && (
				<div {...panelScrollRegionProps} className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
					{groups.map((group) => (
						<div key={group.key} className={group.label ? "mb-2" : undefined}>
							{group.label && (
								<Text type="caption" color="tertiary" className="block px-2 py-1">
									{group.label}
								</Text>
							)}
							{group.items.map((item) => {
								const selected = activeKey === item.key;
								return (
									<button
										key={item.key}
										type="button"
										aria-pressed={selected}
										data-testid={`sfaa-directory-${slug}-option`}
										onClick={() => onSelect(item.key)}
										className={cn(
											"flex w-full items-center justify-between rounded px-2 py-2 text-left hover:bg-surface-light",
											selected && "bg-surface-accent",
										)}
										{...trackingAttrs(`sfaa-directory-${slug}-option`)}
									>
										<Text type="body" color={selected ? "link" : "primary"}>
											{item.label}
										</Text>
										<div className="flex items-center gap-1">
											<Text type="caption" color="tertiary" aria-hidden>
												{item.count}
											</Text>
											{showChevron && <Icon type="chevron-right" size="mini" />}
										</div>
									</button>
								);
							})}
						</div>
					))}
				</div>
			)}
		</section>
	);
};

const CodePane = ({
	activeType,
	activeClass,
	suretyType,
	ownerType,
	onShowTypeColumn,
	onShowClassColumn,
	subclassGroups,
	selectedId,
	onSelect,
}: {
	activeType?: TypeChoice;
	activeClass?: string;
	suretyType: SuretyType;
	ownerType: SfaaContractObligee;
	onShowTypeColumn: () => void;
	onShowClassColumn: () => void;
	subclassGroups: [string, SfaaCodeRow[]][];
	selectedId: SfaaCodeId | null;
	onSelect: (id: SfaaCodeId | null) => void;
}) => (
	<section className="flex min-h-0 min-w-0 flex-1 flex-col">
		{activeType && activeClass && (
			<div className="shrink-0 space-y-1 border-b border-bc-default px-4 py-3">
				<Text type="title" color="primary" className="block">
					{activeClass}
				</Text>
				<div className="flex items-center gap-1">
					<ClickableText
						type="caption"
						color="link"
						aria-label="Show type column"
						testId="sfaa-directory-breadcrumb-type"
						trackingId="sfaa-directory-breadcrumb-type"
						onClick={onShowTypeColumn}
					>
						{activeType.type}
					</ClickableText>
					<Text type="caption" color="tertiary">
						&gt;
					</Text>
					<ClickableText
						type="caption"
						color="link"
						aria-label="Show class column"
						testId="sfaa-directory-breadcrumb-subtype"
						trackingId="sfaa-directory-breadcrumb-subtype"
						onClick={onShowClassColumn}
					>
						{activeType.subType}
					</ClickableText>
				</div>
				{suretyType === SuretyType.contract && (
					<Text type="caption" color="secondary" className="block">
						{`Owner type: ${sfaaContractObligeeLabel(ownerType)}`}
					</Text>
				)}
			</div>
		)}
		<div {...panelScrollRegionProps} className="min-h-0 flex-1 overflow-y-auto">
			{subclassGroups.length === 0 ? (
				<Text type="body" color="tertiary" className="block px-4 py-3">
					No matching codes
				</Text>
			) : (
				<RadioGroup
					value={selectedId}
					onChange={onSelect}
					direction="column"
					className="gap-0"
					aria-label="SFAA code"
				>
					{subclassGroups.map(([subClass, rows]) => (
						<section key={subClass || activeClass}>
							{subClass && (
								<div className="px-4 py-2">
									<Text type="body-strong" color="primary">
										{subClass}
									</Text>
								</div>
							)}
							{rows.map((row) => (
								<CodeRadio key={row.id} row={row} />
							))}
						</section>
					))}
				</RadioGroup>
			)}
		</div>
	</section>
);

const SelectionFooter = ({
	selectedRow,
	onClose,
	onAttach,
}: {
	selectedRow?: SfaaCodeRow;
	onClose: () => void;
	onAttach: (value: SfaaCodeId) => void;
}) => (
	<>
		<div className="min-w-0">
			{selectedRow && (
				<div className="flex min-w-0 items-start gap-2" data-testid="sfaa-directory-selection">
					<FirstLine>
						<OptionBadge badge={selectedRow.code} />
					</FirstLine>
					<div className="flex min-w-0 flex-1 flex-col gap-1">
						<Text type="body" color="primary" truncate>
							{selectedRow.label}
						</Text>
						<Text type="caption" color="tertiary" truncate>
							{codePath(selectedRow)}
						</Text>
					</div>
				</div>
			)}
		</div>
		<div className="flex shrink-0 items-center gap-2">
			<Button
				variant="outline"
				color="secondary"
				size="sm"
				trackingId="sfaa-directory-cancel"
				onClick={onClose}
			>
				Cancel
			</Button>
			<Button
				variant="solid"
				color="primary"
				size="sm"
				trackingId="sfaa-directory-attach"
				disabled={!selectedRow}
				onClick={() => {
					if (selectedRow) onAttach(selectedRow.id);
				}}
			>
				Attach
			</Button>
		</div>
	</>
);

export const SfaaCodeDirectory = ({
	sfaaCodes,
	suretyType,
	initialOwnerType = "Private",
	value,
	onAttach,
	onClose,
}: SfaaCodeDirectoryProps) => {
	const attached = value ? sfaaCodes.find((row) => row.id === value) : undefined;
	const attachedOwner =
		suretyType === SuretyType.contract && attached && isSfaaContractObligee(attached.obligeeType)
			? attached.obligeeType
			: undefined;
	const [ownerType, setOwnerType] = useState<SfaaContractObligee>(
		attachedOwner ?? initialOwnerType,
	);
	const [query, setQuery] = useState("");
	const [selectedTypeKey, setSelectedTypeKey] = useState(
		attached ? typeKeyOf(attached) : undefined,
	);
	const [selectedClass, setSelectedClass] = useState(attached?.class);
	const [selectedId, setSelectedId] = useState<SfaaCodeId | null>(attached?.id ?? null);
	const [typeCollapsed, setTypeCollapsed] = useState(false);
	const [classCollapsed, setClassCollapsed] = useState(false);
	const normalizedQuery = query.trim().toLocaleLowerCase();
	const searching = normalizedQuery !== "";

	const ownerRows = useMemo(
		() => sfaaCodes.filter((row) => matchesSfaaObligee(row.obligeeType, suretyType, ownerType)),
		[suretyType, ownerType, sfaaCodes],
	);

	const selectOwnerType = (nextOwnerType: SfaaContractObligee) => {
		if (nextOwnerType === ownerType) return;
		setOwnerType(nextOwnerType);
		const selected = sfaaCodes.find((row) => row.id === selectedId);
		if (!selected || !isSfaaCodeForOtherOwner(selected.obligeeType, nextOwnerType)) return;
		setSelectedId(equivalentSfaaCode(sfaaCodes, selected, nextOwnerType)?.id ?? null);
	};

	const typeChoices = useMemo(() => {
		const counts = new Map<string, Omit<TypeChoice, "key">>();
		for (const row of ownerRows) {
			const key = typeKeyOf(row);
			const current = counts.get(key);
			if (current) current.count += 1;
			else counts.set(key, { type: row.type, subType: row.subType, count: 1 });
		}
		return [...counts.entries()]
			.map(([key, choice]) => ({ key, ...choice }))
			.sort((a, b) => a.type.localeCompare(b.type) || a.subType.localeCompare(b.subType));
	}, [ownerRows]);

	const typeGroups = useMemo(() => groupBy(typeChoices, (choice) => choice.type), [typeChoices]);
	const typeOutline = useMemo((): { title: string; groups: OutlineGroup[] } => {
		const [first, ...rest] = typeGroups;
		if (!first) return { title: "Type", groups: [] };
		const itemsFor = (choices: TypeChoice[]): OutlineItem[] =>
			choices.map((choice) => ({ key: choice.key, label: choice.subType, count: choice.count }));
		return {
			title: first[0],
			groups: [
				{ key: first[0], items: itemsFor(first[1]) },
				...rest.map(([type, choices]) => ({
					key: type,
					label: type,
					items: itemsFor(choices),
				})),
			],
		};
	}, [typeGroups]);

	const activeTypeKey = typeChoices.some((choice) => choice.key === selectedTypeKey)
		? selectedTypeKey
		: typeChoices[0]?.key;
	const activeType = typeChoices.find((choice) => choice.key === activeTypeKey);

	const classChoices = useMemo(() => {
		const counts = new Map<string, number>();
		for (const row of ownerRows) {
			if (typeKeyOf(row) !== activeTypeKey) continue;
			counts.set(row.class, (counts.get(row.class) ?? 0) + 1);
		}
		return [...counts.entries()]
			.map(([label, count]) => ({ label, count }))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [activeTypeKey, ownerRows]);

	const activeClass = classChoices.some((choice) => choice.label === selectedClass)
		? selectedClass
		: classChoices[0]?.label;

	const leafRows = useMemo(
		() =>
			ownerRows
				.filter((row) => typeKeyOf(row) === activeTypeKey && row.class === activeClass)
				.sort(byCode),
		[activeClass, activeTypeKey, ownerRows],
	);

	const subclassGroups = useMemo(
		() => groupBy(leafRows, (row) => row.subClass).sort(([a], [b]) => a.localeCompare(b)),
		[leafRows],
	);

	const searchGroups = useMemo(() => {
		if (normalizedQuery === "") return [];
		return groupBy(
			ownerRows.filter((row) => matchesSearch(row, normalizedQuery)),
			typeKeyOf,
		)
			.map(([key, rows]) => {
				const first = rows[0];
				return {
					key,
					type: first.type,
					subType: first.subType,
					rows: rows.sort(byCode),
				};
			})
			.sort((a, b) => a.type.localeCompare(b.type) || a.subType.localeCompare(b.subType));
	}, [normalizedQuery, ownerRows]);

	const selectedRow = sfaaCodes.find((row) => row.id === selectedId);

	return (
		<FloatingPanel
			width={960}
			resizable
			className="h-[72vh]"
			onClose={onClose}
			testId="sfaa-code-directory"
			closeTrackingId="sfaa-code-directory-close"
		>
			<DirectoryHeader />
			<FloatingPanel.Body className="flex-col">
				<DirectoryToolbar
					query={query}
					onQueryChange={setQuery}
					suretyType={suretyType}
					ownerType={ownerType}
					onOwnerTypeChange={selectOwnerType}
				/>
				{searching ? (
					<SearchResults
						query={query}
						groups={searchGroups}
						selectedId={selectedId}
						onSelect={setSelectedId}
						ownerLabel={
							suretyType === SuretyType.contract ? sfaaContractObligeeLabel(ownerType) : undefined
						}
					/>
				) : (
					<div className="flex min-h-0 flex-1 divide-x divide-bc-default">
						<OutlineColumn
							name="Type"
							title={typeOutline.title}
							widthClass="w-56"
							collapsed={typeCollapsed}
							onToggle={() => setTypeCollapsed((collapsed) => !collapsed)}
							groups={typeOutline.groups}
							activeKey={activeTypeKey}
							onSelect={(key) => {
								setSelectedTypeKey(key);
								setSelectedClass(undefined);
							}}
						/>
						<OutlineColumn
							name="Class"
							title="Class"
							widthClass="w-64"
							collapsed={classCollapsed}
							onToggle={() => setClassCollapsed((collapsed) => !collapsed)}
							groups={[
								{
									key: "class",
									items: classChoices.map((choice) => ({
										key: choice.label,
										label: choice.label,
										count: choice.count,
									})),
								},
							]}
							activeKey={activeClass}
							onSelect={setSelectedClass}
							showChevron
						/>
						<CodePane
							activeType={activeType}
							activeClass={activeClass}
							suretyType={suretyType}
							ownerType={ownerType}
							onShowTypeColumn={() => setTypeCollapsed(false)}
							onShowClassColumn={() => setClassCollapsed(false)}
							subclassGroups={subclassGroups}
							selectedId={selectedId}
							onSelect={setSelectedId}
						/>
					</div>
				)}
			</FloatingPanel.Body>
			<FloatingPanel.Footer>
				<SelectionFooter selectedRow={selectedRow} onClose={onClose} onAttach={onAttach} />
			</FloatingPanel.Footer>
		</FloatingPanel>
	);
};
